import { FC, useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Modal from '@/components/ui/Modal';
import {
  Button,
  SearchInputExternalResolver
} from 'stubben-edge-unified-frontend-react-components';
import { useGlobalSearch } from '@hooks/useGlobalSearch';
import { transformSearchResultsToOptions } from '@utils/searchUtils';

type MobileSearchModalProps = {
  open: boolean;
  onClose: () => void;
};

const MobileSearchModal: FC<MobileSearchModalProps> = ({ open, onClose }) => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const { data: searchData, isLoading: isSearchLoading } = useGlobalSearch({
    searchTerm,
    enabled: open && searchTerm.length > 0
  });

  const searchOptions = useMemo(() => {
    if (!searchData?.items) return [];
    return transformSearchResultsToOptions(searchData.items);
  }, [searchData]);

  const handleSearchSelect = (optionValue: string | number) => {
    const selectedOption = searchOptions.find(
      (opt) => opt.value === optionValue
    );
    if (selectedOption?.href) {
      // Extract pathname if it's an absolute URL, otherwise use as-is
      const url = selectedOption.href.startsWith('http')
        ? new URL(selectedOption.href).pathname
        : selectedOption.href;
      navigate(url);
      setSearchTerm('');
      onClose();
    }
  };

  const handleCancel = () => {
    setSearchTerm('');
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={handleCancel}
      hideHeader
      size="md"
      cardProps={{
        className:
          '!w-full !h-full !max-h-screen !max-w-full !m-0 !rounded-none flex flex-col lg:hidden'
      }}
      cardBodyProps={{
        className: 'flex flex-col flex-1 overflow-hidden p-4 gap-4'
      }}
    >
      <div className="flex min-h-0 flex-1 flex-col gap-4 p-2">
        <div className="flex items-center gap-4 pt-4">
          <SearchInputExternalResolver
            inputRef={inputRef}
            placeholder="Search for people, tasks..."
            value={searchTerm}
            onChange={setSearchTerm}
            options={searchOptions}
            optionsLoading={isSearchLoading}
            onSelectOption={handleSearchSelect}
            onDeselectOption={() => setSearchTerm('')}
            className="w-full"
            variant="secondary"
          />
          <Button variant="tertiary" onClick={handleCancel}>
            Cancel
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default MobileSearchModal;
